// src/components/MarketingAdmin.tsx
// Módulo de Marketing (Meta) — KPIs, Leads Inbox, Publicador unificado
// Design system: Orion glass-morphism (o-card, o-btn-ghost, --acc1/2/3)

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  BarChart3, DollarSign, Eye, MousePointerClick, Users, TrendingUp,
  Instagram, Facebook, Megaphone, Mail, Phone, User, Search,
  Send, Image, Link as LinkIcon, ToggleLeft, ToggleRight,
  Loader, RotateCw, AlertTriangle, CheckCircle2, ChevronDown,
  Filter, ExternalLink,
} from 'lucide-react';
import {
  getMarketingOverview, getMarketingKpis, getLeadsInbox, publishUnified, clearJwtCache,
  type MarketingOverview, type MarketingKpis, type LeadEntry, type PageInfo, type IgAccount, type PublishRequest,
  ApiError,
} from '@/lib/api_marketing';
import { audit } from '@/lib/audit';
import { BusinessSelector } from '@/components/businesses/BusinessSelector';
import ComposerV2 from '@/components/marketing/ComposerV2';

/* ═══════════════════════════════════════════════════════════════
   TYPES & CONSTANTS
   ═══════════════════════════════════════════════════════════════ */

type TabId = 'dashboard' | 'leads' | 'publish';

const TABS: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <BarChart3 size={14} /> },
  { id: 'leads', label: 'Leads Inbox', icon: <Mail size={14} /> },
  { id: 'publish', label: 'Publicar', icon: <Send size={14} /> },
];

const DATE_PRESETS = [
  { value: 'today', label: 'Hoy' },
  { value: 'yesterday', label: 'Ayer' },
  { value: 'last_7d', label: '7 días' },
  { value: 'last_14d', label: '14 días' },
  { value: 'last_30d', label: '30 días' },
  { value: 'this_month', label: 'Este mes' },
];

/* ═══════════════════════════════════════════════════════════════
   SHARED UI
   ═══════════════════════════════════════════════════════════════ */

function KpiCard({ icon, label, value, sub, color = '--acc1-rgb' }: { icon: React.ReactNode; label: string; value: string | number; sub?: string; color?: string }) {
  const display = typeof value === 'object' ? JSON.stringify(value) : String(value);
  return (
    <div className="o-card p-4 flex items-start gap-3">
      <div className="p-2 rounded-lg" style={{ background: `rgba(var(${color}), 0.12)`, color: `rgba(var(${color}), 1)` }}>{icon}</div>
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-white/40 font-semibold">{label}</p>
        <p className="text-lg font-bold text-white truncate">{display}</p>
        {sub && <p className="text-[10px] text-white/30">{sub}</p>}
      </div>
    </div>
  );
}

function ChannelBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${ok ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
      {ok ? <CheckCircle2 size={10} /> : <AlertTriangle size={10} />} {label}
    </span>
  );
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return <div className="flex flex-col items-center justify-center py-16 text-white/20 gap-3">{icon}<p className="text-sm">{text}</p></div>;
}

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

interface MarketingAdminProps {
  addNotification?: (n: { type: 'success' | 'error' | 'info'; message: string }) => void;
  initialTab?: TabId;
}

export function MarketingAdmin({ addNotification, initialTab }: MarketingAdminProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(initialTab || 'dashboard');

  const [overview, setOverview] = useState<MarketingOverview | null>(null);
  const [kpis, setKpis] = useState<MarketingKpis | null>(null);
  const [datePreset, setDatePreset] = useState('last_7d');

  useEffect(() => { if (initialTab) setActiveTab(initialTab); }, [initialTab]);

  const notify = useCallback((type: 'success' | 'error' | 'info', message: string) => addNotification?.({ type, message }), [addNotification]);

  const num = (v: any): number => { const n = Number(v); return isNaN(n) ? 0 : n; };
  const str = (v: any): string => typeof v === 'object' ? JSON.stringify(v) : String(v ?? '');

  const [errorStatus, setErrorStatus] = useState<number | null>(null);

  const fetchInitial = useCallback(async () => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const [ov, kp] = await Promise.all([
        getMarketingOverview(),
        getMarketingKpis(datePreset),
      ]);
      setOverview(ov);
      setKpis(kp);
    } catch (e: any) {
      setError(e.message);
      setErrorStatus(e instanceof ApiError ? e.status : null);
    } finally {
      setLoading(false);
    }
  }, [datePreset]);

  useEffect(() => { fetchInitial(); }, [fetchInitial]);

  const refreshKpis = useCallback(async (preset: string) => {
    try {
      const kp = await getMarketingKpis(preset);
      setKpis(kp);
    } catch (e: any) {
      notify('error', e.message);
    }
  }, [notify]);

  const handlePresetChange = useCallback((preset: string) => {
    setDatePreset(preset);
    refreshKpis(preset);
  }, [refreshKpis]);

  if (loading) return (
    <div className="max-w-7xl mx-auto flex items-center justify-center py-32 text-white/30 gap-3">
      <Loader size={20} className="animate-spin" /> Cargando módulo de marketing…
    </div>
  );

  if (error) return (
    <div className="max-w-7xl mx-auto">
      <div className="o-card p-8 text-center">
        <AlertTriangle size={32} className={`mx-auto mb-3 ${errorStatus === 403 ? 'text-amber-400' : 'text-red-400'}`} />
        <p className="text-white/70 font-semibold mb-1">
          {errorStatus === 403 ? 'Acceso denegado' : errorStatus === 404 ? 'No encontrado' : errorStatus === 502 ? 'Servicio externo no disponible' : 'Error al cargar marketing'}
        </p>
        <p className="text-sm text-white/50 mb-5">{str(error)}</p>
        {errorStatus !== 403 && (
          <button onClick={() => { clearJwtCache(); fetchInitial(); }} className="o-btn-ghost inline-flex items-center gap-2 px-4 py-2 text-sm"><RotateCw size={14} /> Reintentar</button>
        )}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6 pb-20">
      {/* Header — only when no initialTab (standalone mode) */}
      {!initialTab && (
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Megaphone className="w-6 h-6" style={{ color: 'var(--acc1)' }} /> Marketing Meta
          </h1>
          <p className="text-white/40 text-sm mt-1 ml-8">
            Facebook Ads · Pages · Instagram · Lead Forms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <BusinessSelector compact />
          <button onClick={fetchInitial} className="o-btn-ghost flex items-center gap-1.5 px-3 py-2 text-sm"><RotateCw size={14} /> Recargar</button>
        </div>
      </header>
      )}
      {/* Show selector even when we have a fixed initialTab (sub-route) */}
      {initialTab && (
        <div className="flex justify-end -mb-2"><BusinessSelector compact /></div>
      )}

      {/* Inventory summary cards — show on dashboard tab */}
      {(!initialTab || initialTab === 'dashboard') && overview && (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard icon={<DollarSign size={16} />} label="Cuentas Ads" value={num(overview.summary.ad_accounts)} color="--acc1-rgb" />
        <KpiCard icon={<Facebook size={16} />} label="Pages" value={num(overview.summary.pages)} color="--acc2-rgb" />
        <KpiCard icon={<Instagram size={16} />} label="Instagram" value={num(overview.summary.instagram_accounts)} color="--acc3-rgb" />
        <KpiCard icon={<Mail size={16} />} label="Formularios" value={num(overview.summary.lead_forms)} color="--acc1-rgb" />
      </div>
      )}

      {/* Tabs — only when no initialTab */}
      {!initialTab && (
      <div className="border-b border-white/10">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {TABS.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium rounded-t-lg border-b-2 transition-all whitespace-nowrap
                ${activeTab === tab.id ? 'border-[var(--acc1)] text-[var(--acc1)] bg-white/5' : 'border-transparent text-white/40 hover:text-white/70 hover:bg-white/5'}`}>
              {tab.icon}{tab.label}
            </button>
          ))}
        </nav>
      </div>
      )}

      {/* Content */}
      <div className="min-h-[400px]">
        {activeTab === 'dashboard' && <DashboardPanel kpis={kpis} errors={kpis?.errors || {}} datePreset={datePreset} onPresetChange={handlePresetChange} />}
        {activeTab === 'leads' && <LeadsPanel pages={overview?.pages || []} notify={notify} />}
        {activeTab === 'publish' && (
          <ComposerV2
            addNotification={({ type, title, message }) => notify(type, [title, message].filter(Boolean).join(' — '))}
            onPublished={fetchInitial}
          />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DASHBOARD PANEL — KPIs
   ═══════════════════════════════════════════════════════════════ */

function DashboardPanel({ kpis, errors, datePreset, onPresetChange }: {
  kpis: MarketingKpis | null;
  errors: Record<string, string>;
  datePreset: string;
  onPresetChange: (p: string) => void;
}) {
  if (!kpis) return <EmptyState icon={<BarChart3 size={32} />} text="No hay datos de KPIs disponibles" />;

  const k = kpis.kpis;
  const num = (v: any): number => { const n = Number(v); return isNaN(n) ? 0 : n; };
  const fmt = (v: any) => num(v).toLocaleString('es-MX');
  const money = (v: any) => `$${num(v).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  const pct = (v: any) => `${num(v).toFixed(2)}%`;

  // Solo mostrar el banner cuando el canal trae un valor no vacío. El
  // backend devuelve `errors: { ads: [], pages: [], instagram: [] }`
  // para indicar "no hubo errores" (listas vacías), lo cual la lógica
  // anterior renderizaba como "errores: []" — falso positivo.
  const isMeaningful = (v: any) => {
    if (v == null || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === 'object') return Object.keys(v).length > 0;
    return true;
  };
  const errorChannels = Object.entries(errors)
    .filter(([, v]) => isMeaningful(v))
    .map(([ch]) => ch);

  return (
    <div className="space-y-6">
      {/* Date preset selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {DATE_PRESETS.map((p) => (
          <button key={p.value} onClick={() => onPresetChange(p.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${datePreset === p.value ? 'bg-[var(--acc1)]/15 text-[var(--acc1)] border border-[var(--acc1)]/30' : 'text-white/40 border border-white/10 hover:text-white/60 hover:border-white/20'}`}>
            {p.label}
          </button>
        ))}
      </div>

      {/* Channel errors */}
      {errorChannels.length > 0 && (
        <div className="o-card p-3 border border-amber-500/20 bg-amber-500/5">
          <p className="text-xs text-amber-400 flex items-center gap-2"><AlertTriangle size={14} /> Algunos canales reportan errores:</p>
          {errorChannels.map((ch) => {
            const v = errors[ch];
            const msg = typeof v === 'object' ? JSON.stringify(v) : String(v);
            return <p key={ch} className="text-xs text-white/40 ml-6 mt-1">• <strong>{ch}</strong>: {msg}</p>;
          })}
        </div>
      )}

      {/* Ads KPIs */}
      <div>
        <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2"><DollarSign size={14} style={{ color: 'var(--acc1)' }} /> Facebook Ads</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <KpiCard icon={<DollarSign size={16} />} label="Spend" value={money(k.ads_spend)} color="--acc1-rgb" />
          <KpiCard icon={<Eye size={16} />} label="Impresiones" value={fmt(k.ads_impressions)} color="--acc2-rgb" />
          <KpiCard icon={<MousePointerClick size={16} />} label="Clicks" value={fmt(k.ads_clicks)} color="--acc3-rgb" />
          <KpiCard icon={<Users size={16} />} label="Alcance" value={fmt(k.ads_reach)} color="--acc1-rgb" />
          <KpiCard icon={<TrendingUp size={16} />} label="CTR" value={pct(k.ads_ctr_pct)} color="--acc2-rgb" />
        </div>
      </div>

      {/* Pages KPIs */}
      <div>
        <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2"><Facebook size={14} style={{ color: 'var(--acc2)' }} /> Facebook Pages</h3>
        <div className="grid grid-cols-2 gap-3">
          <KpiCard icon={<Eye size={16} />} label="Impresiones" value={fmt(k.pages_impressions)} color="--acc2-rgb" />
          <KpiCard icon={<Users size={16} />} label="Engaged Users" value={fmt(k.pages_engaged_users)} color="--acc2-rgb" />
        </div>
      </div>

      {/* Instagram KPIs */}
      <div>
        <h3 className="text-sm font-semibold text-white/60 mb-3 flex items-center gap-2"><Instagram size={14} style={{ color: 'var(--acc3)' }} /> Instagram</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <KpiCard icon={<Eye size={16} />} label="Impresiones" value={fmt(k.instagram_impressions)} color="--acc3-rgb" />
          <KpiCard icon={<Users size={16} />} label="Alcance" value={fmt(k.instagram_reach)} color="--acc3-rgb" />
          <KpiCard icon={<User size={16} />} label="Visitas perfil" value={fmt(k.instagram_profile_views)} color="--acc3-rgb" />
          <KpiCard icon={<Users size={16} />} label="Seguidores" value={fmt(k.instagram_follower_count)} color="--acc3-rgb" />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   LEADS INBOX PANEL
   ═══════════════════════════════════════════════════════════════ */

function LeadsPanel({ pages, notify }: { pages: PageInfo[]; notify: (t: any, m: string) => void }) {
  const [selectedPage, setSelectedPage] = useState<string>(pages[0]?.id || '');
  const [formFilter, setFormFilter] = useState<string>('');
  const [leads, setLeads] = useState<LeadEntry[]>([]);
  const [forms, setForms] = useState<{ form_id: string; form_name: string; leads_count?: number; error?: string }[]>([]);
  const [totalLeads, setTotalLeads] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const loadLeads = useCallback(async () => {
    if (!selectedPage) return;
    setLoading(true);
    try {
      const data = await getLeadsInbox({ page_id: selectedPage, form_id: formFilter || undefined, limit_per_form: 50 });
      setLeads(data.leads);
      setForms(data.forms);
      setTotalLeads(data.total_leads);
    } catch (e: any) {
      notify('error', e.message);
    } finally {
      setLoading(false);
    }
  }, [selectedPage, formFilter, notify]);

  useEffect(() => { if (selectedPage) loadLeads(); }, [selectedPage, formFilter, loadLeads]);

  const filtered = useMemo(() => {
    if (!search) return leads;
    const s = search.toLowerCase();
    return leads.filter((l) =>
      (l.customer_name?.toLowerCase().includes(s)) ||
      (l.customer_email?.toLowerCase().includes(s)) ||
      (l.customer_phone?.includes(s)) ||
      (l.form_name?.toLowerCase().includes(s))
    );
  }, [leads, search]);

  if (pages.length === 0) return <EmptyState icon={<Facebook size={32} />} text="No hay páginas de Facebook conectadas" />;

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs text-white/40">Página:</label>
          <select value={selectedPage} onChange={(e) => { setSelectedPage(e.target.value); setFormFilter(''); }}
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none">
            {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        {forms.length > 0 && (
          <div className="flex items-center gap-2">
            <label className="text-xs text-white/40">Formulario:</label>
            <select value={formFilter} onChange={(e) => setFormFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none">
              <option value="">Todos</option>
              {forms.map((f) => <option key={f.form_id} value={f.form_id}>{f.form_name} ({f.leads_count ?? '?'})</option>)}
            </select>
          </div>
        )}

        <div className="flex items-center gap-2 ml-auto">
          <Search size={14} className="text-white/30" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar lead…"
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none w-48" />
        </div>

        <span className="text-xs text-white/30">{totalLeads} leads total</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center gap-2 py-16 justify-center text-white/30 text-sm"><Loader size={16} className="animate-spin" /> Cargando leads…</div>
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Mail size={32} />} text="No hay leads para esta página" />
      ) : (
        <div className="o-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10 text-white/40 text-xs uppercase tracking-wider">
                <th className="text-left p-3">Fecha</th>
                <th className="text-left p-3">Nombre</th>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Teléfono</th>
                <th className="text-left p-3">Formulario</th>
                <th className="text-left p-3">Campaña</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.lead_id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="p-3 text-white/60 text-xs whitespace-nowrap">{new Date(l.created_time).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</td>
                  <td className="p-3 text-white font-medium">{l.customer_name || '—'}</td>
                  <td className="p-3 text-white/70">{l.customer_email || '—'}</td>
                  <td className="p-3 text-white/70">{l.customer_phone || '—'}</td>
                  <td className="p-3 text-white/50 text-xs">{l.form_name}</td>
                  <td className="p-3 text-white/50 text-xs">{l.ad_name || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PUBLISH PANEL — Unified FB + IG
   ═══════════════════════════════════════════════════════════════ */

function PublishPanel({ pages, igAccounts, notify, onPublished }: {
  pages: PageInfo[];
  igAccounts: IgAccount[];
  notify: (t: any, m: string) => void;
  onPublished: () => void;
}) {
  const [pageId, setPageId] = useState(pages[0]?.id || '');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [publishFb, setPublishFb] = useState(true);
  const [publishIg, setPublishIg] = useState(false);
  const [igImageUrl, setIgImageUrl] = useState('');
  const [igCaption, setIgCaption] = useState('');
  const [igAccountId, setIgAccountId] = useState<string>('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; results: Record<string, any>; errors: Record<string, string> } | null>(null);

  const canSend = pageId && message.trim() && (publishFb || publishIg) && (!publishIg || igImageUrl.trim());

  const handlePublish = async () => {
    if (!canSend) return;
    setSending(true);
    setResult(null);
    audit('publish_attempt', { page_id: pageId, fb: publishFb, ig: publishIg });
    try {
      const body: PublishRequest = {
        page_id: pageId,
        message: message.trim(),
        link: link.trim() || undefined,
        publish_to_page: publishFb,
        publish_to_instagram: publishIg,
        ig_account_id: igAccountId || null,
        ig_image_url: publishIg ? igImageUrl.trim() : undefined,
        ig_caption: igCaption.trim() || undefined,
      };
      const res = await publishUnified(body);
      setResult(res);
      if (res.ok) {
        audit('publish_success', { page_id: pageId, fb: publishFb, ig: publishIg });
        notify('success', 'Publicación exitosa');
        onPublished();
      } else {
        const errMsgs = Object.values(res.errors).map((e) => typeof e === 'object' ? JSON.stringify(e) : String(e)).join(', ');
        notify('error', `Errores: ${errMsgs}`);
      }
    } catch (e: any) {
      const status = e instanceof ApiError ? e.status : 0;
      audit('publish_error', { page_id: pageId, status });
      if (status === 403) {
        notify('error', 'Sin permisos para publicar en esta página.');
      } else if (status === 404) {
        notify('error', 'Página no encontrada o no pertenece a tu organización.');
      } else if (status === 502) {
        notify('error', 'Meta API no disponible. Intenta de nuevo en unos minutos.');
      } else {
        notify('error', e.message);
      }
    } finally {
      setSending(false);
    }
  };

  if (pages.length === 0) return <EmptyState icon={<Send size={32} />} text="No hay páginas conectadas para publicar" />;

  return (
    <div className="max-w-2xl space-y-6">
      {/* Page selector */}
      <div>
        <label className="block text-xs text-white/40 mb-1.5">Página de Facebook</label>
        <select value={pageId} onChange={(e) => setPageId(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none">
          {pages.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </div>

      {/* Message */}
      <div>
        <label className="block text-xs text-white/40 mb-1.5">Mensaje</label>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} placeholder="Escribe tu publicación…"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none resize-y" />
      </div>

      {/* Link (optional, for FB) */}
      <div>
        <label className="block text-xs text-white/40 mb-1.5 flex items-center gap-1"><LinkIcon size={12} /> Link (opcional, Facebook)</label>
        <input type="url" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://example.com"
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm outline-none" />
      </div>

      {/* Channel toggles */}
      <div className="flex items-center gap-6">
        <button onClick={() => setPublishFb(!publishFb)} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
          {publishFb ? <ToggleRight size={20} style={{ color: 'var(--acc2)' }} /> : <ToggleLeft size={20} className="text-white/30" />}
          <Facebook size={16} /> Facebook Page
        </button>
        <button onClick={() => setPublishIg(!publishIg)} className="flex items-center gap-2 text-sm text-white/70 hover:text-white transition-colors">
          {publishIg ? <ToggleRight size={20} style={{ color: 'var(--acc3)' }} /> : <ToggleLeft size={20} className="text-white/30" />}
          <Instagram size={16} /> Instagram
        </button>
      </div>

      {/* IG fields */}
      {publishIg && (
        <div className="o-card p-4 space-y-3 border border-[var(--acc3)]/20">
          <h4 className="text-xs font-semibold text-white/50 flex items-center gap-1.5"><Instagram size={14} style={{ color: 'var(--acc3)' }} /> Configuración Instagram</h4>

          {igAccounts.length > 0 && (
            <div>
              <label className="block text-xs text-white/40 mb-1">Cuenta IG (opcional)</label>
              <select value={igAccountId} onChange={(e) => setIgAccountId(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none">
                <option value="">Auto-detectar desde página</option>
                {igAccounts.map((ig) => <option key={ig.id} value={ig.id}>@{ig.username}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs text-white/40 mb-1 flex items-center gap-1"><Image size={12} /> URL de imagen (obligatorio)</label>
            <input type="url" value={igImageUrl} onChange={(e) => setIgImageUrl(e.target.value)} placeholder="https://cdn.example.com/image.jpg"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none" />
            {publishIg && !igImageUrl.trim() && <p className="text-[10px] text-amber-400 mt-1">⚠ Imagen requerida para Instagram</p>}
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-1">Caption IG (opcional, usa mensaje si está vacío)</label>
            <textarea value={igCaption} onChange={(e) => setIgCaption(e.target.value)} rows={2} placeholder="Caption específico para Instagram…"
              className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs outline-none resize-y" />
          </div>
        </div>
      )}

      {/* Send button */}
      <button onClick={handlePublish} disabled={!canSend || sending}
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold transition-all ${canSend && !sending ? 'bg-[var(--acc1)] text-black hover:opacity-90' : 'bg-white/10 text-white/30 cursor-not-allowed'}`}>
        {sending ? <><Loader size={16} className="animate-spin" /> Publicando…</> : <><Send size={16} /> Publicar</>}
      </button>

      {/* Result */}
      {result && (
        <div className={`o-card p-4 border ${result.ok ? 'border-emerald-500/20 bg-emerald-500/5' : 'border-red-500/20 bg-red-500/5'}`}>
          <p className={`text-sm font-semibold ${result.ok ? 'text-emerald-400' : 'text-red-400'} flex items-center gap-2`}>
            {result.ok ? <CheckCircle2 size={16} /> : <AlertTriangle size={16} />}
            {result.ok ? 'Publicación exitosa' : 'Publicación con errores'}
          </p>
          {Object.entries(result.results).map(([ch, r]) => (
            <p key={ch} className="text-xs text-white/50 mt-1 ml-6">✓ <strong>{ch}</strong>: {typeof r === 'object' ? JSON.stringify(r) : String(r)}</p>
          ))}
          {Object.entries(result.errors).map(([ch, err]) => (
            <p key={ch} className="text-xs text-red-400/70 mt-1 ml-6">✗ <strong>{ch}</strong>: {typeof err === 'object' ? JSON.stringify(err) : String(err)}</p>
          ))}
        </div>
      )}
    </div>
  );
}
